import { create } from 'zustand';
import { RachelScopeKeySource, getRachelScopeKey } from '../effects/rachelScope';
import { RACHEL_EFFECT_MS } from '../styles/rachelEffects.styles';

const RACHEL_CHANGE_WINDOW_MS = 5000;

interface RachelScopedKeys {
  scope: RachelScopeKeySource;
  keys: string[];
}

interface RachelScrollRequest {
  keys: string[];
}

interface RachelChangesStore {
  changedAtByScope: Record<string, number>;
  addedKeysByScope: Record<string, string[]>;
  removedKeysByScope: Record<string, string[]>;
  scrollRequestByScope: Record<string, RachelScrollRequest>;
  markChange: (scope: RachelScopeKeySource) => void;
  isChangeActive: (scope: RachelScopeKeySource) => boolean;
  markAdded: (params: RachelScopedKeys) => void;
  markRemoved: (params: RachelScopedKeys) => void;
  requestScroll: (params: RachelScopedKeys) => void;
}

const withScope = <T>(record: Record<string, T>, scope: RachelScopeKeySource, value: T) => ({
  ...record,
  [getRachelScopeKey(scope)]: value,
});

const useRachelChangesStore = create<RachelChangesStore>((set, get) => ({
  changedAtByScope: {},
  addedKeysByScope: {},
  removedKeysByScope: {},
  scrollRequestByScope: {},
  markChange: (scope) =>
    set((state) => ({ changedAtByScope: withScope(state.changedAtByScope, scope, Date.now()) })),
  isChangeActive: (scope) => {
    const changedAt = get().changedAtByScope[getRachelScopeKey(scope)];
    return !!changedAt && Date.now() - changedAt < RACHEL_CHANGE_WINDOW_MS;
  },
  markAdded: ({ scope, keys }) => {
    set((state) => ({ addedKeysByScope: withScope(state.addedKeysByScope, scope, keys) }));
    setTimeout(() => {
      set((state) =>
        state.addedKeysByScope[getRachelScopeKey(scope)] === keys
          ? { addedKeysByScope: withScope(state.addedKeysByScope, scope, []) }
          : state,
      );
    }, RACHEL_EFFECT_MS.highlight);
  },
  markRemoved: ({ scope, keys }) =>
    set((state) => ({ removedKeysByScope: withScope(state.removedKeysByScope, scope, keys) })),
  requestScroll: ({ scope, keys }) =>
    set((state) => ({
      scrollRequestByScope: withScope(state.scrollRequestByScope, scope, { keys }),
    })),
}));

export default useRachelChangesStore;
